import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import Reveal from '../components/ui/Reveal';
import SlotBookingModal from '../components/ui/SlotBookingModal';
import { company } from '../data/company';
import { submitLead } from '../lib/bookDemo';
import './Contact.css';

const topics = ['Sales', 'Support', 'Partnerships', 'Press'];

const SLOT_MINUTES = 30;
const DAY_START_HOUR = 9;
const DAY_END_HOUR = 19;
const DAYS_AHEAD = 7;

const dayLabel = (date) =>
  date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const isoDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const timeLabel = (hour, minute) => {
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const buildDays = () => {
  const days = [];
  const now = new Date();
  for (let i = 0; i < DAYS_AHEAD; i += 1) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    days.push(d);
  }
  return days;
};

const buildSlotsForDay = (date, isToday) => {
  const slots = [];
  const now = new Date();
  for (let h = DAY_START_HOUR; h < DAY_END_HOUR; h += 1) {
    for (let m = 0; m < 60; m += SLOT_MINUTES) {
      if (isToday) {
        const slotTime = new Date(date);
        slotTime.setHours(h, m, 0, 0);
        if (slotTime <= now) continue;
      }
      slots.push({ hour: h, minute: m, label: timeLabel(h, m) });
    }
  }
  return slots;
};

export default function Contact() {
  const [topic, setTopic] = useState('Sales');
  const [wantsDemo, setWantsDemo] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [bookedSlot, setBookedSlot] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [smsConsent, setSmsConsent] = useState(false);

  const days = useMemo(buildDays, []);
  const [dayIndex, setDayIndex] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const slots = useMemo(() => buildSlotsForDay(days[dayIndex], dayIndex === 0), [days, dayIndex]);

  const [activeOfficeKey, setActiveOfficeKey] = useState(company.offices[0].key);
  const activeOffice = company.offices.find((o) => o.key === activeOfficeKey) ?? company.offices[0];

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const selectDay = (i) => {
    setDayIndex(i);
    setSelectedSlot(null);
  };

  const handleBookingConfirmed = (slotDetails) => {
    setBookedSlot(slotDetails);
    setSubmitted(true);
    setSelectedSlot(null);
  };

  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.phone.trim() && !smsConsent) {
      setError('Please check the SMS consent box to continue, or leave the phone field blank.');
      return;
    }

    setSubmitting(true);
    try {
      await submitLead({
        fullName: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        message: form.message.trim() || null,
        topic,
        wantsDemo: false,
        demoDate: null,
        demoStartTime: null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <PageHero
        eyebrow="Contact Us"
        title="Talk to a real person, not a ticket queue."
        body="Sales, support, partnerships, or press — tell us what you need and we'll route it to the right team within one business day."
      />

      <section className="section">
        <div className="container contact-grid">
          <Reveal as="div" className="contact-form-col card">
            <span className="eyebrow">Get in Touch</span>
            <h2>Let’s talk about your field operations.</h2>
            <p>Whether you're evaluating the platform or just have a question — tell us and we'll route you to the right specialist within one business day.</p>

            <div className="contact-direct">
              <a href={`mailto:${company.email}`}>{company.email}</a>
              {company.offices.map((office) => (
                <a key={office.phone} href={`tel:${office.phone.replace(/[^+\d]/g, '')}`}>{office.phone}</a>
              ))}
            </div>

            {submitted ? (
              <div className="contact-success">
                <h3>{bookedSlot ? 'Meeting booked.' : 'Message sent.'}</h3>
                {bookedSlot ? (
                  <p>
                    Your 30-minute demo is set for <strong>{bookedSlot.dayLabel}</strong> at{' '}
                    <strong>{bookedSlot.slotLabel}</strong>. A confirmation email is on its way to{' '}
                    <strong>{bookedSlot.email}</strong>.
                  </p>
                ) : (
                  <p>Someone from our {topic} team will reach out within one business day.</p>
                )}
              </div>
            ) : (
              <>
                <div className="mode-toggle">
                  <button
                    type="button"
                    className={`mode-chip ${wantsDemo ? 'active' : ''}`}
                    onClick={() => setWantsDemo(true)}
                  >
                    <strong>Book a 30-min demo</strong>
                    <span>Pick a time in the next 7 days</span>
                  </button>
                  <button
                    type="button"
                    className={`mode-chip ${!wantsDemo ? 'active' : ''}`}
                    onClick={() => setWantsDemo(false)}
                  >
                    <strong>Just send a message</strong>
                    <span>We'll reply within one business day</span>
                  </button>
                </div>

                <div className="topic-chips">
                  {topics.map((t) => (
                    <button
                      type="button"
                      key={t}
                      className={`topic-chip ${topic === t ? 'active' : ''}`}
                      onClick={() => setTopic(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>

                {wantsDemo ? (
                  <div className="calendar-cell">
                    <span className="cell-label">Choose a day</span>
                    <div className="day-strip">
                      {days.map((d, i) => (
                        <button
                          type="button"
                          key={d.toISOString()}
                          className={`day-chip ${i === dayIndex ? 'active' : ''}`}
                          onClick={() => selectDay(i)}
                        >
                          <span className="day-weekday">{d.toLocaleDateString('en-US', { weekday: 'short' })}</span>
                          <span className="day-num">{d.getDate()}</span>
                        </button>
                      ))}
                    </div>

                    <span className="cell-label">Choose a time · {dayLabel(days[dayIndex])}</span>
                    <div className="slot-grid">
                      {slots.length === 0 && (
                        <p className="no-slots">No more slots today — try another day.</p>
                      )}
                      {slots.map((s) => (
                        <button
                          type="button"
                          key={`${s.hour}-${s.minute}`}
                          className="slot-chip"
                          onClick={() => setSelectedSlot(s)}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                    <p className="slot-hint">Pick a time to open the booking form.</p>
                  </div>
                ) : (
                  <form className="contact-form" onSubmit={handleMessageSubmit}>
                    <label>
                      <span>Full name</span>
                      <input type="text" placeholder="Jordan Lee" value={form.name} onChange={handleChange('name')} required />
                    </label>

                    <label>
                      <span>Work email</span>
                      <input type="email" placeholder="jordan@company.com" value={form.email} onChange={handleChange('email')} required />
                    </label>

                    <label>
                      <span>Company</span>
                      <input type="text" placeholder="Company name" value={form.company} onChange={handleChange('company')} />
                    </label>

                    <label>
                      <span>Phone (optional)</span>
                      <input type="tel" placeholder="+1 (___) ___-____" value={form.phone} onChange={handleChange('phone')} />
                    </label>

                    {form.phone.trim() && (
                      <label className="sms-consent-label">
                        <input
                          type="checkbox"
                          checked={smsConsent}
                          onChange={(e) => setSmsConsent(e.target.checked)}
                        />
                        <span>
                          By checking this box, I agree to receive SMS messages from Tecnvirons
                          (such as appointment reminders and service updates) at the phone number
                          provided. Message frequency varies and may be recurring. Message and data
                          rates may apply. Reply STOP to opt out at any time, or HELP for help. See
                          our <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and{' '}
                          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                        </span>
                      </label>
                    )}

                    <label>
                      <span>How can we help?</span>
                      <textarea rows="4" placeholder="Tell us about your team and what you're looking for..." value={form.message} onChange={handleChange('message')} required />
                    </label>

                    {error && <p className="form-error">{error}</p>}

                    <button type="submit" className="btn btn-primary contact-submit" disabled={submitting}>
                      {submitting ? 'Sending…' : 'Send message'}
                    </button>
                  </form>
                )}
              </>
            )}
          </Reveal>

          <Reveal as="div" stagger className="contact-offices">
            <span className="eyebrow">Our Offices</span>
            <h2>Find us in person.</h2>

            <div className="office-tabs">
              {company.offices.map((office) => (
                <button
                  key={office.key}
                  type="button"
                  className={`office-tab ${office.key === activeOfficeKey ? 'active' : ''}`}
                  onClick={() => setActiveOfficeKey(office.key)}
                >
                  {office.city}
                </button>
              ))}
            </div>

            <div className="map-frame reveal-item">
              <iframe
                key={activeOffice.key}
                title={`Map showing Tecnvirons ${activeOffice.city} office`}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(activeOffice.mapQuery)}&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            {company.offices.map((office) => (
              <div key={office.label} className="office-card card reveal-item">
                <h3>{office.label}</h3>
                <p>{office.legalName}, {office.address}</p>
                <div className="office-meta">
                  <span>Hours: {office.hours}</span>
                  <span>Phone: {office.phone}</span>
                </div>
              </div>
            ))}

            <a href={company.whatsappLink} target="_blank" rel="noopener noreferrer" className="whatsapp-card card reveal-item">
              <div>
                <h3>Chat with us</h3>
                <p>Questions about the platform? Message us directly on WhatsApp.</p>
              </div>
              <span className="btn btn-secondary">Message us on WhatsApp</span>
            </a>
          </Reveal>
        </div>
      </section>

      {wantsDemo && selectedSlot && (
        <SlotBookingModal
          topic={topic}
          dayLabel={dayLabel(days[dayIndex])}
          isoDate={isoDate(days[dayIndex])}
          slot={selectedSlot}
          onClose={() => setSelectedSlot(null)}
          onConfirmed={handleBookingConfirmed}
        />
      )}
    </div>
  );
}
